import { docStats, findNode, type AuthoringDoc } from '@personalizehq/editor';
import {
  blockSchema,
  experienceDocumentSchema,
  MAX_LAYOUT_DEPTH,
  MAX_LAYOUT_NODES,
  type SurfaceKind,
} from '@personalizehq/schemas';
import type { BlockNode, Size, SizeVal, Theme } from '@personalizehq/schemas/ir';

// Edit-time law surfacing (spec §4). THE LAWS ARE NOT RE-IMPLEMENTED HERE — `blockSchema` is the
// same strict gatekeeper `publish` runs, and its issue paths already point at fields
// (`['size','width']`, `['aspect_ratio']`, `['spans',0,'fallback']`). Re-stating "custom may not
// hug" in the inspector would be a second copy of the law, free to drift from the one that decides
// whether a document can ship. So: parse, and map paths onto widgets.
//
// Two things the node's own parse cannot see, and only those two are hand-written:
//
//   1. PARENT-CONDITIONED laws. `size.height: "fill"` is legal in isolation and illegal because the
//      parent is a vertical scroll (nothing to divide — the axis is unbounded) or a grid. Those
//      issues are raised by the PARENT's superRefine, at `['children', i, 'size', …]`.
//   2. TOKEN RESOLUTION. `checkTokenRefs` runs at the document level, not the block level.

/** field path (`size.width`, `spans.0.fallback`) → message. */
export type FieldIssues = Record<string, string>;

/** A dangling token WARNS but does not block save — a draft may reference a token the author is one
 *  keystroke from creating, and refusing to save that is hostile. `authoringDocSchema` encodes the
 *  same asymmetry server-side. Publish remains the law. */
export function nodeIssues(node: BlockNode, parent: BlockNode | null, theme: Theme): FieldIssues {
  const issues: FieldIssues = {};

  const own = blockSchema.safeParse(node);
  if (!own.success) {
    for (const issue of own.error.issues) {
      const path = issue.path;
      // Issues under a child, a fallback or a repeat template belong to THAT node's inspector.
      if (path[0] === 'children' || path[0] === 'fallback' || path[0] === 'template') continue;
      // A button's spans live under `label`; one widget renders both, and it keys on `spans.*`.
      const field = (path[0] === 'label' ? ['spans', ...path.slice(1)] : path).join('.');
      issues[field] ??= issue.message;
    }
  }

  if (parent) {
    const parsed = blockSchema.safeParse(parent);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const [head, index, ...rest] = issue.path;
        if (head !== 'children' || typeof index !== 'number') continue;
        if ((parent as { children?: BlockNode[] }).children?.[index]?.id !== node.id) continue;
        issues[rest.join('.')] ??= issue.message;
      }
    }
  }

  const token = (field: string, value: string | null | undefined, dict: Record<string, unknown>): void => {
    if (value === null || value === undefined || value in dict) return;
    issues[field] ??= `“${value}” is not a token in this theme`;
  };

  // STRICTER than the schema, deliberately. Zod accepts `fallback: ""` (a string is a string), but
  // an empty fallback is a hole in a sentence wearing a disguise — the exact failure `ref` spans
  // carry a fallback to prevent. The editor is where that gets caught, since the wire cannot.
  const spans = node.block === 'text' ? node.spans : node.block === 'button' ? node.label : [];
  spans.forEach((span, index) => {
    if (span.kind === 'ref' && span.fallback.trim() === '') {
      issues[`spans.${index}.fallback`] ??= 'a variable needs a fallback — the value can be missing at runtime';
    }
  });

  // `bg` is a token OR a gradient whose STOPS are tokens (SPEC §5.0c). A check that only handled
  // the string would leave an unresolvable stop to be discovered at publish — or, worse, not at
  // all: an unknown token renders a documented default in the field, silently, forever.
  const bg = node.style?.bg;
  if (typeof bg === 'string' || bg === undefined) {
    token('style.bg', bg, theme.colors);
  } else {
    bg.stops.forEach((stop, i) => token(`style.bg.stops.${i}`, stop, theme.colors));
  }
  token('style.border.color', node.style?.border?.color, theme.colors);
  token('style.shadow.color', node.style?.shadow?.color, theme.colors);

  switch (node.block) {
    case 'text':
      token('color', node.color, theme.colors);
      token('font', node.font, theme.fonts);
      break;
    case 'button':
      token('variant', node.variant, theme.buttons);
      token('font', node.font, theme.fonts);
      break;
    case 'image':
      token('tint', node.tint, theme.colors);
      break;
    case 'progress':
      token('fill_color', node.fill_color, theme.colors);
      token('track_color', node.track_color, theme.colors);
      break;
    default:
      break;
  }

  return issues;
}

// ---------------------------------------------------------------------------- size legality

export type SizeMode = 'fixed' | 'fill' | 'hug' | 'auto';

/** What the ROOT's legality needs and a block cannot supply: the step it is the root OF. Optional —
 *  without it `sizeOptions` simply cannot see the root's own law, which is exactly what the panel did
 *  before this existed. */
export interface StepCtx {
  rootId: string;
  surface: SurfaceKind;
  theme: Theme;
}

/** What the panel offers per axis, and whether the width may carry a max/min bound. */
export interface SizeLegality {
  width: SizeMode[];
  height: SizeMode[];
  /** `max_width`/`min_width` are a bound on a RESOLVED width — meaningless against a fixed one. */
  bounds: boolean;
}

/** Apply one axis edit exactly as `SizeField` would, so what we PROBE is what the author would get.
 *
 *  CLEARING AN AXIS MEANS CLEARING THAT AXIS, and nothing else. It used to mean something worse:
 *  `width` is the only non-optional member of `Size`, so "auto" on the width deleted the WHOLE
 *  object — silently taking a `height: fill` down with it. The author changed a field they were
 *  looking at and lost one they were not, the layout collapsed, and ⌘Z could not separate the two
 *  because both edits shared a merge key. It is how the onboarding's step 1 lost its height.
 *
 *  So a width-clear now keeps the rest of the object — which makes it an INVALID `Size` whenever
 *  anything else is set, and that is exactly the point: `sizeOptions` probes this candidate against
 *  the real schema, sees `width` is required, and simply does not OFFER `auto`. The destructive
 *  option stops existing rather than being survived. */
function withAxis(size: Size | undefined, axis: 'width' | 'height', value: SizeVal | undefined): Size | undefined {
  if (axis === 'width') {
    if (value !== undefined) return { ...size, width: value };
    const rest = { ...size } as Partial<Size>;
    delete rest.width;
    // Nothing else on the object → clearing the width really is clearing the size. Otherwise this
    // is a width-less Size, which the schema rejects, which is the honest answer.
    return Object.keys(rest).length === 0 ? undefined : (rest as Size);
  }

  const patch: Size = { ...size, width: size?.width ?? 'fill', height: value };
  if (value === undefined) delete patch.height;
  return patch;
}

/** Would this size publish? THE LAW IS NOT RE-STATED — we hand the real gatekeeper a candidate and
 *  read its answer. Only issues rooted at `size` count: the node may be unpublishable for reasons
 *  that have nothing to do with its width (a dangling token, an empty `ref` fallback), and those
 *  must not blank out the size dropdown. */
function sizePublishes(
  node: BlockNode,
  parent: BlockNode | null,
  size: Size | undefined,
  step: StepCtx | undefined
): boolean {
  const candidate = { ...node } as BlockNode & { size?: Size };
  if (size === undefined) delete candidate.size;
  else candidate.size = size;

  const own = blockSchema.safeParse(candidate);
  if (!own.success && own.error.issues.some((issue) => issue.path[0] === 'size')) return false;

  // THE ROOT HAS A LAW OF ITS OWN, and it is invisible to `blockSchema`. A root `height: "fill"`
  // means the VIEWPORT (SPEC §6.1c), so it is legal only on a `fullscreen` step — on a bottom sheet
  // it would anchor the sheet to the top of the screen and collapse its detents. `surface` and
  // `root` only meet at the DOCUMENT, so that is the gatekeeper we hand the candidate to. We do not
  // restate "fill only on fullscreen" here; we ask.
  if (step && node.id === step.rootId) {
    const doc = { schema_version: 'v1', surface: step.surface, theme: step.theme, root: candidate };
    const parsed = experienceDocumentSchema.safeParse(doc);
    if (
      !parsed.success &&
      parsed.error.issues.some((issue) => issue.path[0] === 'root' && issue.path[1] === 'size')
    ) {
      return false;
    }
    return true; // the root has no parent to consult
  }

  // The parent-conditioned laws — `fill` along a HUGGING scroll axis, `fill` height in a grid —
  // are raised by the PARENT's superRefine at `['children', i, 'size', …]` and are invisible to the
  // child's own parse. This is the half `modes()` structurally could not express: today they only
  // appear as an error AFTER the author picks a value the panel offered them.
  const children = parent && 'children' in parent ? (parent.children as BlockNode[]) : undefined;
  const index = children?.findIndex((child) => child.id === node.id) ?? -1;
  if (!parent || !children || index < 0) return true;

  const withChild = { ...parent, children: children.map((c, i) => (i === index ? candidate : c)) };
  const above = blockSchema.safeParse(withChild);
  if (above.success) return true;
  return !above.error.issues.some(
    (issue) => issue.path[0] === 'children' && issue.path[1] === index && issue.path[2] === 'size'
  );
}

const PROBE = [
  ['fixed', 100],
  ['fill', 'fill'],
  ['hug', 'hug'],
] as const satisfies readonly (readonly [SizeMode, SizeVal])[];

/** Which size modes this block may actually use, per axis — PROBED against `blockSchema`, never
 *  hand-copied from it. The predecessor (`inspector-fields.tsx`'s `modes()`/`canClear()`) restated
 *  five size schemas by hand and said so in its own docstring; Phase A then made it worse by adding
 *  `max_width`/`min_width`, which are legal on all five and illegal against a fixed width. A copy of
 *  the law is a copy free to drift from the one that decides whether a document can ship.
 *
 *  `auto` is simply "may this axis be ABSENT" — which for `custom`, `progress` and `grid` it may
 *  not: each declares a NON-optional size, so clearing the width there authors a document that
 *  cannot be published. The TS type says `size?:` on every block. The TS type is not the law. */
export function sizeOptions(node: BlockNode, parent: BlockNode | null, step?: StepCtx): SizeLegality {
  const axis = (name: 'width' | 'height'): SizeMode[] => {
    const modes: SizeMode[] = [];
    if (sizePublishes(node, parent, withAxis(node.size, name, undefined), step)) modes.push('auto');
    for (const [mode, value] of PROBE) {
      if (sizePublishes(node, parent, withAxis(node.size, name, value), step)) modes.push(mode);
    }
    return modes;
  };

  // A bound needs a width to bound. (Not a restatement of the law — `width` is required by `Size`,
  // so with no width there is no object to put a `max_width` on.) Whether it is then LEGAL is the
  // schema's call, as ever.
  const bounds =
    node.size?.width !== undefined &&
    sizePublishes(node, parent, { ...node.size, max_width: 200 }, step);

  return { width: axis('width'), height: axis('height'), bounds };
}

export interface Bounds {
  nodes: number;
  depth: number;
  /** True at 80% of either bound — the meter turns amber BEFORE the author hits a wall. */
  near: boolean;
  over: boolean;
}

export function stepBounds(doc: AuthoringDoc, stepId: string): Bounds {
  const step = doc.steps.find((s) => s.id === stepId);
  const { nodes, depth } = step ? docStats(step.root) : { nodes: 0, depth: 0 };
  return {
    nodes,
    depth,
    near: nodes > MAX_LAYOUT_NODES * 0.8 || depth > MAX_LAYOUT_DEPTH * 0.8,
    over: nodes > MAX_LAYOUT_NODES || depth > MAX_LAYOUT_DEPTH,
  };
}

/** The error-count chip in the top bar: every node in the focused step, structural issues only.
 *  Walks with `findNode`'s notion of the editable tree — see `nodeIssues` for why fallback/template
 *  issues are not this function's business. */
export function stepIssueCount(doc: AuthoringDoc, stepId: string): number {
  const step = doc.steps.find((s) => s.id === stepId);
  if (!step) return 0;

  let count = 0;
  const walk = (node: BlockNode): void => {
    const parent = findNode(step.root, node.id)?.parent ?? null;
    count += Object.keys(nodeIssues(node, parent, doc.theme)).length;
    if ('children' in node) node.children.forEach(walk);
  };
  walk(step.root);
  return count;
}
